import { Component } from '@theme/component';
import { debounce, requestIdleCallback } from '@theme/utilities';

/**
 * Premium anchored popover controller.
 *
 * Handles:
 * - Trigger/popover positioning
 * - Hover interactions
 * - Resize closing
 * - External vertical anchors
 * - Dialog handoff
 * - Focus restoration
 *
 * @typedef {object} Refs
 * @property {HTMLElement} popover
 * @property {HTMLElement} trigger
 * @property {HTMLElement} [verticalPositionAnchor]
 *
 * @extends Component<Refs>
 */
export class AnchoredPopoverComponent extends Component {
  requiredRefs = ['popover', 'trigger'];

  /** Delay used for hover-open / hover-close interactions. */
  interaction_delay = 200;

  /** @type {number | null} */
  #popoverTrigger = null;

  /** @type {AbortController | null} */
  #controller = null;

  /**
   * Opens the hover popover after the interaction delay.
   */
  #onTriggerEnter = () => {
    const { trigger, popover } = this.refs;

    trigger.dataset.hoverActive = 'true';

    this.#clearPopoverTimer();

    if (popover.matches(':popover-open')) return;

    this.#popoverTrigger = window.setTimeout(() => {
      this.#popoverTrigger = null;

      if (!trigger.matches('[data-hover-active]')) return;

      popover.showPopover();
    }, this.interaction_delay);
  };

  /**
   * Starts closing the popover when leaving the trigger.
   */
  #onTriggerLeave = () => {
    const { trigger, popover } = this.refs;

    delete trigger.dataset.hoverActive;

    this.#clearPopoverTimer();

    if (!popover.matches(':popover-open')) return;

    this.#popoverTrigger = window.setTimeout(() => {
      this.#popoverTrigger = null;

      if (popover.matches(':popover-open')) {
        popover.hidePopover();
      }
    }, this.interaction_delay);
  };

  /**
   * Cancels closing while the pointer is inside the popover.
   */
  #onPopoverEnter = () => {
    this.#clearPopoverTimer();
  };

  /**
   * Starts closing after leaving the popover.
   */
  #onPopoverLeave = () => {
    const { popover } = this.refs;

    this.#clearPopoverTimer();

    this.#popoverTrigger = window.setTimeout(() => {
      this.#popoverTrigger = null;

      if (popover.matches(':popover-open')) {
        popover.hidePopover();
      }
    }, this.interaction_delay);
  };

  /**
   * Clears any pending hover interaction.
   */
  #clearPopoverTimer() {
    if (this.#popoverTrigger === null) return;

    window.clearTimeout(this.#popoverTrigger);
    this.#popoverTrigger = null;
  }

  /**
   * Calculates fallback positioning values.
   */
  #updatePosition = () => {
    const { popover, trigger } = this.refs;

    if (!popover || !trigger) return;

    const triggerRect = trigger.getBoundingClientRect();

    const verticalPositionAnchor = this.#getVerticalPositionAnchor();
    const verticalRect = (verticalPositionAnchor ?? trigger).getBoundingClientRect();

    popover.style.setProperty('--anchor-top', `${verticalRect.top}px`);
    popover.style.setProperty(
      '--anchor-right',
      `${window.innerWidth - triggerRect.right}px`
    );
    popover.style.setProperty(
      '--anchor-bottom',
      `${window.innerHeight - verticalRect.bottom}px`
    );
    popover.style.setProperty('--anchor-left', `${triggerRect.left}px`);
    popover.style.setProperty('--anchor-height', `${verticalRect.height}px`);
    popover.style.setProperty('--anchor-width', `${triggerRect.width}px`);
  };

  /**
   * Gets the preferred vertical positioning anchor.
   *
   * @returns {HTMLElement | null}
   */
  #getVerticalPositionAnchor() {
    const internalAnchor = this.refs.verticalPositionAnchor;

    if (internalAnchor instanceof HTMLElement) {
      return internalAnchor;
    }

    const anchorName = this.dataset.verticalPositionAnchor?.trim();

    if (!anchorName) return null;

    const selector = `[data-anchor-name="${CSS.escape(anchorName)}"]`;

    const scopedAnchor = this.#queryVerticalPositionAnchor(selector);

    if (this.#isUsableExternalVerticalPositionAnchor(scopedAnchor)) {
      return scopedAnchor;
    }

    const root = this.getRootNode();

    const anchor =
      root instanceof Document || root instanceof ShadowRoot
        ? root.querySelector(selector)
        : null;

    return this.#isUsableExternalVerticalPositionAnchor(anchor)
      ? anchor
      : null;
  }

  /**
   * Looks for an anchor in the nearest logical scope.
   *
   * @param {string} selector
   * @returns {HTMLElement | null}
   */
  #queryVerticalPositionAnchor(selector) {
    const scope = this.closest(
      '[data-anchor-scope], tr, li, section, article, form'
    );

    if (!(scope instanceof HTMLElement)) return null;

    const anchor = scope.querySelector(selector);

    return anchor instanceof HTMLElement ? anchor : null;
  }

  /**
   * Checks whether an external anchor can actually be measured.
   *
   * @param {Element | null} anchor
   * @returns {anchor is HTMLElement}
   */
  #isUsableExternalVerticalPositionAnchor(anchor) {
    if (!(anchor instanceof HTMLElement)) return false;

    const rect = anchor.getBoundingClientRect();

    return rect.width > 0 && rect.height > 0;
  }

  /**
   * Closes the popover on resize when requested by markup.
   */
  #resizeListener = debounce(() => {
    const { popover } = this.refs;

    if (!popover?.matches(':popover-open')) return;

    popover.hidePopover();
  }, 100);

  /**
   * Controls the resize listener only while the popover is open.
   *
   * @param {Event} event
   */
  #handleBeforeToggle = (event) => {
    const toggleEvent = /** @type {ToggleEvent} */ (event);

    if (toggleEvent.newState === 'open') {
      this.#updatePosition();

      window.addEventListener('resize', this.#resizeListener);
      return;
    }

    window.removeEventListener('resize', this.#resizeListener);
  };

  /**
   * Opens a dialog from a disclosure trigger without leaving
   * the hover popover active.
   *
   * @param {string} dialogId
   * @param {Event} event
   */
  openDialog(dialogId, event) {
    event.preventDefault();

    const { popover, trigger } = this.refs;
    const triggerAriaControls = trigger.getAttribute('aria-controls');

    this.#clearPopoverTimer();

    delete trigger.dataset.hoverActive;

    if (popover.matches(':popover-open')) {
      popover.hidePopover();
    }

    const dialog =
      /** @type {(HTMLElement & { showDialog?: () => void }) | null} */ (
        document.getElementById(dialogId)
      );

    if (typeof dialog?.showDialog !== 'function') return;

    dialog.addEventListener(
      'dialog:close',
      () => {
        const focusTarget = trigger.isConnected
          ? trigger
          : getTriggerByAriaControls(triggerAriaControls);

        focusTarget?.focus();
      },
      { once: true }
    );

    dialog.showDialog();
  }

  /**
   * Initializes the component.
   */
  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();

    const { signal } = this.#controller;
    const { popover, trigger } = this.refs;

    if (this.dataset.closeOnResize !== undefined) {
      popover.addEventListener('beforetoggle', this.#handleBeforeToggle, {
        signal,
      });
    }

    if (this.dataset.hoverTriggered !== undefined) {
      trigger.addEventListener('pointerenter', this.#onTriggerEnter, {
        signal,
      });

      trigger.addEventListener('pointerleave', this.#onTriggerLeave, {
        signal,
      });

      popover.addEventListener('pointerenter', this.#onPopoverEnter, {
        signal,
      });

      popover.addEventListener('pointerleave', this.#onPopoverLeave, {
        signal,
      });
    }

    /*
     * Older browsers without CSS Anchor Positioning
     * use the calculated custom properties instead.
     */
    if (!CSS.supports('position-anchor: --trigger')) {
      popover.addEventListener(
        'beforetoggle',
        () => {
          this.#updatePosition();
        },
        { signal }
      );

      requestIdleCallback(() => {
        if (!this.isConnected) return;

        this.#updatePosition();
      });
    }
  }

  /**
   * Cleans up listeners/timers.
   */
  disconnectedCallback() {
    this.#clearPopoverTimer();

    this.#controller?.abort();
    this.#controller = null;

    window.removeEventListener('resize', this.#resizeListener);

    super.disconnectedCallback();
  }
}

if (!customElements.get('anchored-popover-component')) {
  customElements.define(
    'anchored-popover-component',
    AnchoredPopoverComponent
  );
}

/**
 * Finds a live disclosure trigger matching an aria-controls value.
 *
 * @param {string | null} ariaControls
 * @returns {HTMLElement | null}
 */
function getTriggerByAriaControls(ariaControls) {
  if (!ariaControls) return null;

  const trigger = document.querySelector(
    `[aria-controls="${CSS.escape(ariaControls)}"]`
  );

  return trigger instanceof HTMLElement && trigger.isConnected
    ? trigger
    : null;
}